


import React, { useMemo, useRef } from 'react';
import { useStore } from '../../context/StoreContext';
import { 
  FileText, Printer, PieChart, BarChart4, 
  TrendingUp, ShoppingBag, DollarSign, Package,
  Calendar, MapPin, User, ChevronRight
} from 'lucide-react';
// FIX: Import Order and OrderStatus types
import { Order, OrderStatus } from '../../types';

const ManagementAI: React.FC = () => {
  // FIX: Destructure 'orders', 'products', 'business' from useStore
  const { orders, products, business } = useStore();
  const reportRef = useRef<HTMLDivElement>(null);
  
  const revenueToday = useMemo(() => orders.reduce((sum, o) => sum + o.total, 0), [orders]);
  const ticketMedio = useMemo(() => orders.length > 0 ? revenueToday / orders.length : 0, [orders, revenueToday]);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="max-w-7xl mx-auto space-y-12 pb-32 animate-fade-in">
      <style>{`
        @media print {
          body * { visibility: hidden; background: white !important; color: black !important; }
          #printable-report, #printable-report * { visibility: visible; }
          #printable-report { 
            position: absolute; left: 0; top: 0; width: 100%; 
            padding: 40px; border: none; box-shadow: none;
          }
          .no-print { display: none !important; }
        }
      `}</style>

      <header className="flex flex-col md:flex-row justify-between items-end gap-8 mb-16 no-print">
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/5 rounded-[2rem] flex items-center justify-center border border-white/10 shadow-premium">
               <FileText className="w-8 h-8 text-rose-500" />
            </div>
            <div>
              <h1 className="text-6xl font-black text-white tracking-tighter uppercase italic leading-none">
                CENTRAL DE <span className="text-rose-500">RELATÓRIOS</span>
              </h1>
              <p className="text-gray-500 font-bold uppercase text-[10px] tracking-[0.5em] mt-2 italic">Exportação e Análise de Performance</p>
            </div>
          </div>
        </div>
        <button 
          onClick={handlePrint}
          className="bg-white text-black px-10 py-5 rounded-full font-black text-xs uppercase tracking-widest flex items-center gap-3 shadow-premium hover:bg-rose-500 hover:text-white transition-all"
        >
          <Printer className="w-5 h-5" /> Gerar Relatório PDF
        </button>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 no-print">
          <StatCard title="Vendas Hoje" value={`R$ ${revenueToday.toFixed(2)}`} icon={DollarSign} color="text-green-500" />
          <StatCard title="Ticket Médio" value={`R$ ${ticketMedio.toFixed(2)}`} icon={TrendingUp} color="text-blue-500" />
          <StatCard title="Total Pedidos" value={orders.length.toString()} icon={ShoppingBag} color="text-rose-500" />
      </div>

      <div className="bg-white/5 border border-white/5 rounded-[4rem] p-12 no-print">
          <div className="flex items-center gap-4 mb-8">
              <PieChart className="w-6 h-6 text-rose-500" />
              <h3 className="text-2xl font-black text-white italic uppercase tracking-tighter">Resumo Operacional</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="p-8 bg-black/40 rounded-3xl border border-white/5">
                  <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Produtos Ativos</p>
                  <p className="text-3xl font-black text-white">{products.filter(p => p.active).length}</p>
              </div>
              <div className="p-8 bg-black/40 rounded-3xl border border-white/5">
                  <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Pedidos Pendentes</p>
                  <p className="text-3xl font-black text-white">{orders.filter(o => o.status === 'PENDING').length}</p>
              </div>
              <div className="p-8 bg-black/40 rounded-3xl border border-white/5">
                  <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Pedidos Entregues</p>
                  <p className="text-3xl font-black text-white">{orders.filter(o => o.status === 'COMPLETED').length}</p>
              </div>
              <div className="p-8 bg-black/40 rounded-3xl border border-white/5">
                  <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Nicho de Atuação</p>
                  <p className="text-xl font-black text-rose-500 uppercase">{business?.niche}</p>
              </div>
          </div>
      </div>

      {/* Relatório Oculto Formatado para Impressão */}
      <div id="printable-report" className="hidden print:block bg-white text-black font-sans p-10 border border-gray-100 rounded-3xl">
          <div className="flex justify-between items-center border-b-2 border-gray-900 pb-8 mb-10">
              <div>
                  {/* FIX: Changed business.name to business.username to match InstagramProfile type */}
                  <h1 className="text-4xl font-black uppercase italic tracking-tighter">{business?.username}</h1>
                  <p className="text-xs font-bold uppercase tracking-widest text-gray-500">Relatório Consolidado de Gestão • Fluxil Engine</p>
              </div>
              <div className="text-right">
                  <p className="text-xs font-black uppercase tracking-widest">{new Date().toLocaleDateString('pt-BR')}</p>
                  <p className="text-xs font-medium text-gray-500">Extração de Dados Oficiais</p>
              </div>
          </div>

          <div className="grid grid-cols-3 gap-8 mb-12">
              <div className="p-6 bg-gray-50 rounded-2xl border border-gray-100">
                  <p className="text-[9px] font-black uppercase text-gray-400 mb-1">Faturamento Bruto</p>
                  <p className="text-2xl font-black">R$ {revenueToday.toFixed(2)}</p>
              </div>
              <div className="p-6 bg-gray-50 rounded-2xl border border-gray-100">
                  <p className="text-[9px] font-black uppercase text-gray-400 mb-1">Total de Pedidos</p>
                  <p className="text-2xl font-black">{orders.length}</p>
              </div>
              <div className="p-6 bg-gray-50 rounded-2xl border border-gray-100">
                  <p className="text-[9px] font-black uppercase text-gray-400 mb-1">Ticket Médio</p>
                  <p className="text-2xl font-black">R$ {ticketMedio.toFixed(2)}</p>
              </div>
          </div>

          <div className="space-y-10">
              <section>
                  <h3 className="text-lg font-black uppercase italic border-b border-gray-200 pb-2 mb-4">Listagem de Pedidos</h3>
                  <table className="w-full text-left text-xs">
                      <thead>
                          <tr className="border-b-2 border-gray-100 text-gray-400 font-black uppercase">
                              <th className="py-3">ID</th>
                              <th className="py-3">Cliente</th>
                              <th className="py-3">Pagamento</th>
                              <th className="py-3">Status</th>
                              <th className="py-3 text-right">Valor</th>
                          </tr>
                      </thead>
                      <tbody>
                          {orders.map(o => (
                              <tr key={o.id} className="border-b border-gray-50">
                                  <td className="py-3 font-mono">#{o.shortId}</td>
                                  <td className="py-3 font-bold">{o.customerName}</td>
                                  <td className="py-3 text-[9px] font-bold uppercase">{o.paymentMethod}</td>
                                  <td className="py-3 uppercase text-[9px] font-black">{o.status}</td>
                                  <td className="py-3 text-right font-bold">R$ {o.total.toFixed(2)}</td>
                              </tr>
                          ))}
                      </tbody>
                  </table>
              </section>
          </div>

          <div className="mt-20 pt-10 border-t border-gray-200 text-center">
              <p className="text-[9px] font-black uppercase tracking-widest text-gray-400">Fluxil Engine • Relatório Gerencial de Alta Performance</p>
          </div>
      </div>
    </div>
  );
};

const StatCard = ({ title, value, icon: Icon, color }: any) => (
    <div className="bg-white/5 border border-white/10 p-10 rounded-[3rem] shadow-premium">
        <div className="flex justify-between items-start mb-6">
            <div className={`w-12 h-12 rounded-2xl bg-white/5 flex items-center justify-center ${color}`}>
                <Icon className="w-6 h-6" />
            </div>
        </div>
        <div>
            <p className="text-[9px] font-black text-gray-500 uppercase tracking-widest mb-1">{title}</p>
            <p className="text-4xl font-black text-white tracking-tighter italic">{value}</p>
        </div>
    </div>
);

export default ManagementAI;